import { FormEvent, useEffect, useMemo, useState } from "react";
import { EmptyState, ErrorState, FreshnessBar, LoadingState } from "../components/AdminPageStates";
import {
  COST_CATEGORIES,
  FounderFinanceDashboardData,
  MarketplaceHealthMetric,
  MarketplaceHealthSummary,
  MarketplaceHealthWarning,
  MarketplaceReleaseQueueEntry,
  MarketplaceReleaseQueueStatus,
  FounderFinanceOperatingCostRow,
  FounderFinanceRevenuePipelineRow,
  FounderFinanceStageSummary,
  FounderFinanceWindowSummary,
  FounderKpiCard,
  FounderKpiValueFormat,
  OperatingCostCategory,
  PIPELINE_STAGES,
  RevenuePipelineStage,
  UpsertFounderFinanceOperatingCostInput
} from "../services/founderFinanceTypes";

const PIPELINE_COPY: Record<RevenuePipelineStage, { title: string; kicker: string; explanation: string; valueLabel: string }> = {
  pending: {
    title: "Pending bookings",
    kicker: "Still cancellable",
    explanation: "Booked revenue that can still fall away.",
    valueLabel: "At-risk net revenue"
  },
  past_cancellation_window: {
    title: "Past cancellation window",
    kicker: "Less likely to cancel",
    explanation: "Bookings that are harder to unwind.",
    valueLabel: "Less-risk net revenue"
  },
  awaiting_payout: {
    title: "Awaiting payout",
    kicker: "Completed, not released",
    explanation: "Completed work waiting for funds to finalize.",
    valueLabel: "Expected net revenue"
  },
  paid_out: {
    title: "Paid-out earnings",
    kicker: "Finalized",
    explanation: "Revenue Chor has already earned.",
    valueLabel: "Realized net revenue"
  }
};

type Signal = "green" | "yellow" | "red";
type AnswerSignal = Signal | "neutral";

type CostTableRow = FounderFinanceOperatingCostRow & {
  label: string;
};

function formatCents(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value)
    ? new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value / 100)
    : "Unavailable";
}

function formatNumber(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value)
    : "Unavailable";
}

function formatPercent(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value)
    ? new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 1 }).format(value)
    : "Unavailable";
}

function formatKpiValue(value: number | null, format: FounderKpiValueFormat) {
  if (format === "money_cents") {
    return formatCents(value);
  }
  if (format === "percent") {
    return formatPercent(value);
  }
  return formatNumber(value);
}

function formatGrowthLabel(card: FounderKpiCard) {
  if (card.growthRate === null) {
    return card.growthLabel === "New" ? "New" : "N/A";
  }
  const prefix = card.growthRate > 0 ? "+" : "";
  return `${prefix}${card.growthRate.toFixed(1)}%`;
}

function centsToDollarInput(value: number | null) {
  return value === null ? "" : (value / 100).toFixed(2);
}

function dollarInputToCents(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
}

function nullableDollarInputToCents(value: string) {
  return value.trim().length === 0 ? null : dollarInputToCents(value);
}

function formatDate(value: string | null) {
  if (!value) {
    return "Unknown";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleDateString();
}

function formatDateTime(value: string | null) {
  if (!value) {
    return "Not updated";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not updated" : date.toLocaleString();
}

function normalizedStripePayoutStatus(row: FounderFinanceRevenuePipelineRow) {
  return row.stripePayoutStatus?.trim().toLowerCase() ?? "";
}

function stripePayoutIsInTransit(row: FounderFinanceRevenuePipelineRow) {
  return Boolean(row.stripePayoutId) && ["pending", "in_transit"].includes(normalizedStripePayoutStatus(row));
}

function stripePayoutIsPaid(row: FounderFinanceRevenuePipelineRow) {
  return Boolean(row.stripePayoutId) && ["paid", "succeeded"].includes(normalizedStripePayoutStatus(row));
}

function payoutTruthLabel(row: FounderFinanceRevenuePipelineRow) {
  if (stripePayoutIsPaid(row)) {
    return "Deposited";
  }
  if (stripePayoutIsInTransit(row)) {
    return "Deposit on the way";
  }
  if (row.stripePayoutId) {
    return "Payout initiated";
  }
  if (row.transferId) {
    return "Transfer created";
  }
  return row.payoutStatus ?? "Unavailable";
}

function dateFromIso(value: string | null) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

type MoneyLifecycleStatus = "complete" | "pending" | "needs_attention" | "unavailable";

type MoneyLifecycleStep = {
  id: string;
  title: string;
  timestamp: string | null;
  amount: number | null;
  status: MoneyLifecycleStatus;
  detail: string;
  diagnostics: Array<{ label: string; value: string }>;
};

function moneyLifecycleStatusLabel(status: MoneyLifecycleStatus) {
  switch (status) {
    case "complete":
      return "Complete";
    case "needs_attention":
      return "Needs attention";
    case "unavailable":
      return "Unavailable";
    default:
      return "Pending";
  }
}

function isOpportunityHire(row: FounderFinanceRevenuePipelineRow) {
  return row.sourceType === "opportunity_hire";
}

function bookingMoneyLifecycle(row: FounderFinanceRevenuePipelineRow): MoneyLifecycleStep[] {
  const isRefunded = row.financeStatus === "refunded";
  const isCanceled = row.financeStatus === "canceled";
  const isBlocked = isRefunded || isCanceled;
  const opportunityHire = isOpportunityHire(row);
  const sourceTitle = opportunityHire ? "Opportunity" : "Booking";
  const providerTitle = opportunityHire ? "Talent" : "Provider";
  const heldDetail = opportunityHire ? "Talent compensation is held by Chor." : "Provider earnings are held by Chor.";
  const releaseDetail = opportunityHire ? "Talent compensation has been released." : "Provider earnings have been released.";
  const releasePendingDetail = opportunityHire ? "Talent compensation has not been released yet." : "Provider earnings have not been released yet.";
  const heldOrLater = ["held", "eligible", "released", "transferred", "paid_out"].includes(row.financeStatus);
  const eligibleOrLater = ["eligible", "released", "transferred", "paid_out"].includes(row.financeStatus);
  const releasedOrLater = ["released", "transferred", "paid_out"].includes(row.financeStatus);
  const transferredOrLater = row.financeStatus === "transferred" || row.financeStatus === "paid_out" || Boolean(row.transferId);
  const payoutCreated = Boolean(row.stripePayoutId);
  const payoutInTransit = stripePayoutIsInTransit(row);
  const paidOut = stripePayoutIsPaid(row);

  return [
    {
      id: "booked",
      title: opportunityHire ? "Opportunity Funded" : "Booked",
      timestamp: row.createdAt,
      amount: row.customerPaidAmount,
      status: "complete",
      detail: opportunityHire ? "Creator funded the Opportunity engagement." : "Booking entered the marketplace money flow.",
      diagnostics: [
        { label: `${sourceTitle} ID`, value: row.sourceId ?? row.bookingId },
        { label: "Marketplace Transaction", value: row.marketplaceTransactionId ?? "Unavailable" }
      ]
    },
    {
      id: "payment-received",
      title: "Payment Received",
      timestamp: row.createdAt,
      amount: row.customerPaidAmount,
      status: row.customerPaidAmount > 0 ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: row.customerPaidAmount > 0 ? "Customer payment is recorded." : "Payment is not confirmed.",
      diagnostics: [
        { label: "Payment Intent", value: row.paymentIntentId ?? "Unavailable" },
        { label: "Stripe Processing Fee", value: formatCents(row.stripeProcessingFeeAmount) },
        { label: "Platform Fee", value: formatCents(row.platformFeeAmount) }
      ]
    },
    {
      id: "funds-held",
      title: "Funds Held",
      timestamp: row.createdAt,
      amount: row.coachGrossEarningsAmount,
      status: heldOrLater ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: heldOrLater ? heldDetail : "Funds are not currently held.",
      diagnostics: [
        { label: "Funds Status", value: row.fundsStatus ?? row.financeStatus },
        { label: "Connected Account", value: row.connectedAccountId ?? "Unavailable" }
      ]
    },
    {
      id: "lesson-completed",
      title: opportunityHire ? "Work Completed" : "Lesson Completed",
      timestamp: row.completedAt,
      amount: null,
      status: eligibleOrLater ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: eligibleOrLater ? `Completion made this ${sourceTitle.toLowerCase()} eligible for release.` : "Completion has not been recorded for release.",
      diagnostics: [
        { label: `${sourceTitle} Status`, value: row.bookingStatus ?? "Unavailable" },
        { label: "Payable Unit", value: row.payableUnitTitle ?? row.payableUnitId ?? "Parent engagement" }
      ]
    },
    {
      id: "funds-released",
      title: "Funds Released",
      timestamp: row.releaseDate,
      amount: row.coachReleasedAmount,
      status: releasedOrLater ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: releasedOrLater ? releaseDetail : releasePendingDetail,
      diagnostics: [
        { label: "Release Date", value: formatDateTime(row.releaseDate) },
        { label: "Transfer Status", value: row.transferStatus ?? "Unavailable" }
      ]
    },
    {
      id: "transfer-created",
      title: "Transfer Created",
      timestamp: row.releaseDate,
      amount: row.coachReleasedAmount,
      status: transferredOrLater ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: transferredOrLater ? "Stripe transfer is recorded." : "No Stripe transfer is recorded yet.",
      diagnostics: [
        { label: "Transfer ID", value: row.transferId ?? "Unavailable" },
        { label: "Connected Account", value: row.connectedAccountId ?? "Unavailable" }
      ]
    },
    {
      id: "deposit-scheduled",
      title: payoutInTransit ? "Deposit on the way" : "Payout initiated",
      timestamp: row.stripePayoutCreatedAt,
      amount: payoutCreated ? row.coachReleasedAmount : null,
      status: payoutCreated ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: payoutInTransit ? `Stripe reports the ${providerTitle.toLowerCase()} payout is on the way.` : "No Stripe payout has been created yet.",
      diagnostics: [
        { label: "Stripe Payout ID", value: row.stripePayoutId ?? "Unavailable" },
        { label: "Stripe Payout Status", value: row.stripePayoutStatus ?? "Unavailable" },
        { label: "Expected Arrival", value: formatDateTime(row.stripePayoutArrivalAt) }
      ]
    },
    {
      id: "paid-out",
      title: "Deposited",
      timestamp: row.payoutDate,
      amount: paidOut ? row.coachReleasedAmount : null,
      status: paidOut ? "complete" : isBlocked ? "needs_attention" : "pending",
      detail: paidOut ? `Stripe reports this ${providerTitle.toLowerCase()} payout was paid.` : `Stripe has not confirmed a paid ${providerTitle.toLowerCase()} payout.`,
      diagnostics: [
        { label: "Stripe Payout ID", value: row.stripePayoutId ?? "Unavailable" },
        { label: "Stripe Payout Status", value: row.stripePayoutStatus ?? "Unavailable" },
        { label: "Payout Date", value: formatDateTime(row.payoutDate) }
      ]
    }
  ];
}

function BookingMoneyLifecycle({ row }: { row: FounderFinanceRevenuePipelineRow }) {
  return (
    <ol className="booking-money-lifecycle" aria-label={`Money lifecycle for booking ${row.bookingId}`}>
      {bookingMoneyLifecycle(row).map((step) => (
        <li key={step.id} className={`booking-money-lifecycle-step ${step.status}`}>
          <div className="booking-money-lifecycle-step-header">
            <strong>{step.title}</strong>
            <span>{moneyLifecycleStatusLabel(step.status)}</span>
          </div>
          <p>{step.detail}</p>
          <dl>
            <div>
              <dt>Timestamp</dt>
              <dd>{formatDateTime(step.timestamp)}</dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd>{step.amount === null ? "Unavailable" : formatCents(step.amount)}</dd>
            </div>
            {step.diagnostics.map((diagnostic) => (
              <div key={`${step.id}-${diagnostic.label}`}>
                <dt>{diagnostic.label}</dt>
                <dd>{diagnostic.value}</dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
    </ol>
  );
}

function monthKeyFromWindow(summary: FounderFinanceWindowSummary) {
  return summary.startsAt.slice(0, 7);
}

function bookingCountLabel(count: number) {
  return `${count} booking${count === 1 ? "" : "s"}`;
}

function stageLabel(stage: RevenuePipelineStage) {
  return PIPELINE_COPY[stage].title;
}

function confidenceSignal(row: FounderFinanceWindowSummary): Signal {
  if (row.projectedCost <= 0 || row.projectedRevenue > row.projectedCost) {
    return "green";
  }
  return row.projectedRevenue >= Math.round(row.projectedCost * 0.9) ? "yellow" : "red";
}

function confidenceLabel(indicator: Signal) {
  if (indicator === "green") {
    return "On track";
  }
  if (indicator === "yellow") {
    return "Near break-even";
  }
  return "Costs ahead";
}

function marketplaceHealthSignalLabel(signal: MarketplaceHealthSummary["signal"]) {
  if (signal === "healthy") {
    return "Healthy";
  }
  if (signal === "warning") {
    return "Warning";
  }
  return "Critical";
}

function confidenceDetail(indicator: Signal) {
  if (indicator === "green") {
    return "Revenue is forecast to cover costs.";
  }
  if (indicator === "yellow") {
    return "Close enough to watch carefully.";
  }
  return "Costs are forecast above revenue.";
}

function emptyStageSummary(stage: RevenuePipelineStage): FounderFinanceStageSummary {
  return {
    stage,
    bookingCount: 0,
    grossAmount: 0,
    stripeFeeAmount: 0,
    appleFeeAmount: 0,
    platformFeeAmount: 0,
    providerPayoutAmount: 0,
    netPlatformRevenue: 0,
    weightedNetPlatformRevenue: 0
  };
}

function stageSummary(data: FounderFinanceDashboardData, stage: RevenuePipelineStage) {
  return data.revenuePipelineByStage[stage] ?? emptyStageSummary(stage);
}

function hasBookings(data: FounderFinanceDashboardData) {
  return PIPELINE_STAGES.some((stage) => stageSummary(data, stage).bookingCount > 0);
}

function currentMonthCostRows(data: FounderFinanceDashboardData): CostTableRow[] {
  const monthKey = monthKeyFromWindow(data.currentMonthSummary);
  const existingRows = data.operatingCostsByMonth.find((month) => month.monthKey === monthKey)?.rows ?? [];
  const existingByCategory = new Map(existingRows.map((row) => [row.category, row]));

  return COST_CATEGORIES.map((category) => ({
    id: existingByCategory.get(category.id)?.id ?? `${monthKey}_${category.id}`,
    monthKey,
    category: category.id,
    label: category.label,
    estimatedAmount: existingByCategory.get(category.id)?.estimatedAmount ?? 0,
    actualAmount: existingByCategory.get(category.id)?.actualAmount ?? null,
    notes: existingByCategory.get(category.id)?.notes ?? "",
    updatedBy: existingByCategory.get(category.id)?.updatedBy ?? null,
    updatedAt: existingByCategory.get(category.id)?.updatedAt ?? null
  }));
}

function hasOperatingCosts(rows: CostTableRow[]) {
  return rows.some((row) => row.estimatedAmount > 0 || row.actualAmount !== null || Boolean(row.notes?.trim()) || row.updatedAt !== null);
}

function answerCopy(row: FounderFinanceWindowSummary, hasForecast: boolean) {
  if (!hasForecast) {
    return {
      indicator: "neutral" as AnswerSignal,
      headline: "Waiting for finance data",
      detail: "Forecast will appear once bookings or costs exist."
    };
  }

  const indicator = confidenceSignal(row);
  if (indicator === "green") {
    return {
      indicator,
      headline: "Chor is projected to cover costs",
      detail: `Projected net this month: ${formatCents(row.projectedNet)}`
    };
  }
  if (indicator === "yellow") {
    return {
      indicator,
      headline: "Chor is close to break-even",
      detail: `Projected net this month: ${formatCents(row.projectedNet)}`
    };
  }
  return {
    indicator,
    headline: "Costs are ahead of revenue",
    detail: `Projected net this month: ${formatCents(row.projectedNet)}`
  };
}

function KpiCard({
  detail,
  indicator,
  label,
  value
}: {
  detail: string;
  indicator?: Signal;
  label: string;
  value: string;
}) {
  return (
    <section className={indicator ? `finance-kpi-card ${indicator}` : "finance-kpi-card"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </section>
  );
}

function formatHours(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(1)} hr` : "Unavailable";
}

function formatHealthMetricValue(metric: MarketplaceHealthMetric) {
  if (metric.valueKind === "money_cents") {
    return typeof metric.value === "number" ? formatCents(metric.value) : "Unavailable";
  }
  if (metric.valueKind === "duration_hours") {
    return typeof metric.value === "number" ? formatHours(metric.value) : "Unavailable";
  }
  if (metric.valueKind === "date") {
    return typeof metric.value === "string" ? formatDateTime(metric.value) : "Unavailable";
  }
  if (metric.valueKind === "count") {
    return typeof metric.value === "number" ? formatNumber(metric.value) : "0";
  }
  return metric.value === null ? "Unavailable" : String(metric.value);
}

function MarketplaceHealthWarningRow({ warning }: { warning: MarketplaceHealthWarning }) {
  const details = [
    warning.bookingId ? `Booking ${warning.bookingId}` : null,
    warning.marketplaceTransactionId ? `Transaction ${warning.marketplaceTransactionId}` : null,
    warning.amount !== null ? formatCents(warning.amount) : null,
    warning.ageHours !== null ? formatHours(warning.ageHours) : null
  ].filter(Boolean);

  return (
    <li className={`marketplace-warning-row ${warning.signal}`}>
      <div>
        <strong>{warning.title}</strong>
        <p>{warning.detail}</p>
        {details.length > 0 ? <small>{details.join(" • ")}</small> : null}
      </div>
      <span>{marketplaceHealthSignalLabel(warning.signal)}</span>
    </li>
  );
}

function MarketplaceHealthPanel({ health }: { health: MarketplaceHealthSummary }) {
  return (
    <section className={`finance-section marketplace-health-section ${health.signal}`}>
      <div className="finance-section-header">
        <div>
          <p className="eyebrow">Marketplace Health</p>
          <h2>{health.headline}</h2>
        </div>
        <span className={`finance-signal ${health.signal === "healthy" ? "green" : health.signal === "warning" ? "yellow" : "red"}`}>
          {marketplaceHealthSignalLabel(health.signal)}
        </span>
      </div>
      <p className="marketplace-health-updated">Generated {formatDateTime(health.generatedAt)}</p>
      <div className="marketplace-health-grid">
        {health.metrics.map((metric) => (
          <section className={`marketplace-health-card ${metric.signal}`} key={metric.id}>
            <span>{metric.title}</span>
            <strong>{formatHealthMetricValue(metric)}</strong>
            <small>{metric.detail}</small>
          </section>
        ))}
      </div>
      <div className="marketplace-warning-block">
        <div className="section-table-header">
          <div>
            <p className="card-title">Warnings</p>
            <h3>Operational payout issues</h3>
          </div>
        </div>
        {health.warnings.length === 0 ? (
          <p className="finance-empty-state">No marketplace money warnings.</p>
        ) : (
          <ul className="marketplace-warning-list">
            {health.warnings.slice(0, 12).map((warning) => (
              <MarketplaceHealthWarningRow
                key={`${warning.kind}-${warning.bookingId ?? "unknown"}-${warning.marketplaceTransactionId ?? "none"}`}
                warning={warning}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

type ReleaseQueueFilter = "all" | "held_too_long" | "failures" | MarketplaceReleaseQueueStatus;
type ReleaseQueueSort = "priority" | "oldest" | "amount" | "attempts";

const RELEASE_QUEUE_FILTERS: Array<{ id: ReleaseQueueFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "held", label: "Held" },
  { id: "eligible", label: "Eligible" },
  { id: "releasing", label: "Releasing" },
  { id: "transferred", label: "Transferred" },
  { id: "paid_out", label: "Paid Out" },
  { id: "failed", label: "Failed" },
  { id: "canceled", label: "Canceled" },
  { id: "refunded", label: "Refunded" },
  { id: "held_too_long", label: "Held too long" },
  { id: "failures", label: "Failures" }
];

function releaseQueueStatusLabel(status: MarketplaceReleaseQueueStatus) {
  const labels: Record<MarketplaceReleaseQueueStatus, string> = {
    held: "Held",
    eligible: "Eligible",
    releasing: "Releasing",
    transferred: "Transferred",
    paid_out: "Paid Out",
    failed: "Failed",
    canceled: "Canceled",
    refunded: "Refunded"
  };
  return labels[status];
}

function dateSortValue(value: string | null) {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
}

function releaseQueuePriority(entry: MarketplaceReleaseQueueEntry) {
  const priorities: Record<MarketplaceReleaseQueueStatus, number> = {
    failed: 0,
    eligible: entry.signal === "critical" ? 1 : 2,
    releasing: 3,
    held: 4,
    transferred: 5,
    paid_out: 6,
    canceled: 7,
    refunded: 8
  };
  return priorities[entry.status];
}

function releaseQueueSearchText(entry: MarketplaceReleaseQueueEntry) {
  return [
    entry.bookingId,
    entry.marketplaceTransactionId,
    entry.coachUserId,
    entry.buyerUserId,
    entry.transferId,
    entry.connectedAccountId,
    entry.failureReason,
    entry.fundsStatus,
    entry.transferStatus,
    releaseQueueStatusLabel(entry.status)
  ].filter(Boolean).join(" ").toLowerCase();
}

function nextSchedulerText(entry: MarketplaceReleaseQueueEntry) {
  if (entry.nextSchedulerAttemptAt) {
    return formatDateTime(entry.nextSchedulerAttemptAt);
  }
  if (entry.status === "eligible") {
    return "Next scheduler run";
  }
  if (entry.status === "releasing") {
    return "Lease pending";
  }
  if (entry.status === "held") {
    return "Not eligible yet";
  }
  if (entry.status === "failed") {
    return "Review required";
  }
  return "Not applicable";
}

function MarketplaceReleaseQueuePanel({ entries }: { entries: MarketplaceReleaseQueueEntry[] }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ReleaseQueueFilter>("all");
  const [sort, setSort] = useState<ReleaseQueueSort>("priority");

  const visibleEntries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return entries
      .filter((entry) => {
        if (filter === "held_too_long") {
          return entry.status === "eligible" && entry.signal === "critical";
        }
        if (filter === "failures") {
          return entry.status === "failed" || entry.signal === "critical";
        }
        if (filter !== "all") {
          return entry.status === filter;
        }
        return true;
      })
      .filter((entry) => normalizedSearch.length === 0 || releaseQueueSearchText(entry).includes(normalizedSearch))
      .sort((first, second) => {
        if (sort === "oldest") {
          return dateSortValue(first.sortAt ?? first.heldSince) - dateSortValue(second.sortAt ?? second.heldSince);
        }
        if (sort === "amount") {
          return second.amount - first.amount;
        }
        if (sort === "attempts") {
          return second.releaseAttempts - first.releaseAttempts;
        }
        const priorityDelta = releaseQueuePriority(first) - releaseQueuePriority(second);
        if (priorityDelta !== 0) {
          return priorityDelta;
        }
        return dateSortValue(first.sortAt ?? first.heldSince) - dateSortValue(second.sortAt ?? second.heldSince);
      });
  }, [entries, filter, searchTerm, sort]);

  return (
    <section className="finance-section marketplace-release-queue-section">
      <div className="finance-section-header">
        <div>
          <p className="eyebrow">Release Queue</p>
          <h2>Every payout waiting in the release pipeline</h2>
        </div>
        <p>Search, sort, and filter payouts from canonical booking and marketplace transaction truth.</p>
      </div>

      <div className="filter-panel release-queue-filter-panel">
        <label>
          Search
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Booking, coach, transfer, account"
          />
        </label>
        <label>
          Filter
          <select value={filter} onChange={(event) => setFilter(event.target.value as ReleaseQueueFilter)}>
            {RELEASE_QUEUE_FILTERS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>
        <label>
          Sort
          <select value={sort} onChange={(event) => setSort(event.target.value as ReleaseQueueSort)}>
            <option value="priority">Operational priority</option>
            <option value="oldest">Oldest first</option>
            <option value="amount">Largest amount</option>
            <option value="attempts">Most release attempts</option>
          </select>
        </label>
      </div>

      {visibleEntries.length === 0 ? (
        <p className="finance-empty-state">No payout queue entries match these filters.</p>
      ) : (
        <div className="table-shell">
          <table className="admin-table marketplace-release-queue-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Booking</th>
                <th>Coach</th>
                <th>Amount</th>
                <th>Held since</th>
                <th>Eligible since</th>
                <th>Next scheduler attempt</th>
                <th>Transfer ID</th>
                <th>Connected account</th>
                <th>Release attempts</th>
                <th>Failure reason</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <span className={`finance-signal ${entry.signal === "healthy" ? "green" : entry.signal === "warning" ? "yellow" : "red"}`}>
                      {releaseQueueStatusLabel(entry.status)}
                    </span>
                  </td>
                  <td>
                    {entry.bookingId}
                    {entry.marketplaceTransactionId ? <span>{entry.marketplaceTransactionId}</span> : null}
                  </td>
                  <td>{entry.coachUserId ?? "Unavailable"}</td>
                  <td>{formatCents(entry.amount)}</td>
                  <td>{formatDateTime(entry.heldSince)}</td>
                  <td>{entry.eligibleSince ? formatDateTime(entry.eligibleSince) : "Not eligible yet"}</td>
                  <td>{nextSchedulerText(entry)}</td>
                  <td>{entry.transferId ?? "Not transferred"}</td>
                  <td>{entry.connectedAccountId ?? "Missing"}</td>
                  <td>{formatNumber(entry.releaseAttempts)}</td>
                  <td>{entry.failureReason ?? "None"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FounderGoalTracker({ data }: { data: FounderFinanceDashboardData }) {
  const tracker = data.founderKpis.goalTracker;
  const projectedValue = tracker.projectedRevenueByDeadline === null
    ? "Not enough data yet"
    : formatCents(tracker.projectedRevenueByDeadline);
  const runRateDetail = tracker.runRateWindowDays === null
    ? "Needs trailing 30-day or 7-day platform revenue."
    : `${formatCents(tracker.runRateDailyAverage)} / day from trailing ${tracker.runRateWindowDays} days`;

  return (
    <section className="founder-goal-card" aria-label="Founder revenue goal tracker">
      <div className="founder-goal-main">
        <p className="eyebrow">$1M Revenue Goal</p>
        <h2>{formatCents(tracker.targetPlatformRevenue)} by Dec 31, 2026</h2>
        <p>{tracker.detail}</p>
      </div>
      <div className="founder-goal-grid">
        <div>
          <span>Current YTD platform revenue</span>
          <strong>{formatCents(tracker.currentYtdPlatformRevenue)}</strong>
        </div>
        <div>
          <span>Remaining needed</span>
          <strong>{formatCents(tracker.remainingRevenueNeeded)}</strong>
        </div>
        <div>
          <span>Required daily average</span>
          <strong>{tracker.requiredDailyAverage === null ? "N/A" : `${formatCents(tracker.requiredDailyAverage)} / day`}</strong>
        </div>
        <div>
          <span>Projected Dec 31 revenue</span>
          <strong>{projectedValue}</strong>
        </div>
        <div>
          <span>Current run rate</span>
          <strong>{runRateDetail}</strong>
        </div>
      </div>
    </section>
  );
}

function FounderKpiTrend({ card }: { card: FounderKpiCard }) {
  const direction = card.direction === "up" ? "↑" : card.direction === "down" ? "↓" : card.direction === "flat" ? "→" : "•";
  const label = card.growthRate === null
    ? card.priorMonthValue === null
      ? "Not enough data yet"
      : formatGrowthLabel(card)
    : `${formatGrowthLabel(card)} MoM`;

  return (
    <span className={`founder-kpi-trend ${card.direction}`}>
      <span aria-hidden="true">{direction}</span>
      {label}
    </span>
  );
}

function FounderKpiDashboardCard({ card }: { card: FounderKpiCard }) {
  const hasCurrentValue = card.currentValue !== null;
  const priorValue = card.priorMonthValue === null ? "N/A" : formatKpiValue(card.priorMonthValue, card.valueFormat);
  const value = hasCurrentValue ? formatKpiValue(card.currentValue, card.valueFormat) : "Not enough data yet";
  const note = card.state === "empty" && card.emptyMessage ? card.emptyMessage : card.detail;

  return (
    <section className={`founder-kpi-dashboard-card ${card.state}`}>
      <div className="founder-kpi-heading">
        <p>{card.title}</p>
        <FounderKpiTrend card={card} />
      </div>
      <strong>{value}</strong>
      <dl>
        <div>
          <dt>{card.priorMonthLabel}</dt>
          <dd>{priorValue}</dd>
        </div>
        <div>
          <dt>Growth</dt>
          <dd>{formatGrowthLabel(card)}</dd>
        </div>
      </dl>
      <small>{note}</small>
    </section>
  );
}

function PipelineCard({
  isSelected,
  onSelect,
  summary
}: {
  isSelected: boolean;
  onSelect: () => void;
  summary: FounderFinanceStageSummary;
}) {
  const copy = PIPELINE_COPY[summary.stage];

  return (
    <button
      type="button"
      className={isSelected ? "finance-pipeline-card active" : "finance-pipeline-card"}
      onClick={onSelect}
    >
      <span className="finance-card-kicker">{copy.kicker}</span>
      <span className="card-title">{copy.title}</span>
      <span className="finance-card-description">{copy.explanation}</span>
      <span className="finance-card-value">{formatCents(summary.netPlatformRevenue)}</span>
      <span className="finance-card-label">{copy.valueLabel}</span>
      <span className="finance-card-lines">
        <span>Gross funded work: {formatCents(summary.grossAmount)}</span>
        <span>Talent/provider payouts: {formatCents(summary.providerPayoutAmount)}</span>
        <span>{bookingCountLabel(summary.bookingCount)} / weighted revenue {formatCents(summary.weightedNetPlatformRevenue)}</span>
      </span>
    </button>
  );
}

function BookingTable({ rows }: { rows: FounderFinanceRevenuePipelineRow[] }) {
  if (rows.length === 0) {
    return <p className="empty-inline">No funded bookings or Opportunities are currently visible for this pipeline stage.</p>;
  }

  return (
    <div className="table-shell">
      <table className="admin-table founder-finance-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th>Booking / Opportunity</th>
            <th>Provider / Talent</th>
            <th>Buyer / Creator</th>
            <th>Start</th>
            <th>Cancel deadline</th>
            <th>Completed</th>
            <th>Status</th>
            <th>Payout</th>
            <th>Gross</th>
            <th>Platform fee</th>
            <th>Fees</th>
            <th>Net revenue</th>
            <th>Talent / provider payout</th>
            <th>Timeline</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.bookingId}>
              <td>{stageLabel(row.stage)}</td>
              <td>
                {isOpportunityHire(row) ? "Opportunity hire" : row.offeringType ?? "Booking"}
                <span>{row.payableUnitTitle ?? row.sourceId ?? row.bookingId}</span>
              </td>
              <td>{row.providerUserId ?? "Unavailable"}</td>
              <td>{row.buyerUserId ?? "Unavailable"}</td>
              <td>{formatDate(row.scheduledStartAt)}</td>
              <td>{formatDate(row.cancellationWindowEndsAt)}</td>
              <td>{formatDate(row.completedAt)}</td>
              <td>{row.bookingStatus ?? "Unavailable"}</td>
              <td>{payoutTruthLabel(row)}</td>
              <td>{formatCents(row.grossAmount)}</td>
              <td>{formatCents(row.platformFeeAmount)}</td>
              <td>{formatCents(row.stripeFeeAmount + row.appleFeeAmount)}</td>
              <td>{formatCents(row.netPlatformRevenue)}</td>
              <td>{formatCents(row.providerPayoutAmount)}</td>
              <td>
                <BookingMoneyLifecycle row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostEditorRow({
  isSaving,
  onSave,
  row
}: {
  isSaving: boolean;
  onSave: (input: UpsertFounderFinanceOperatingCostInput) => void;
  row: CostTableRow;
}) {
  const [estimatedAmount, setEstimatedAmount] = useState(centsToDollarInput(row.estimatedAmount));
  const [actualAmount, setActualAmount] = useState(centsToDollarInput(row.actualAmount));
  const [notes, setNotes] = useState(row.notes ?? "");

  useEffect(() => {
    setEstimatedAmount(centsToDollarInput(row.estimatedAmount));
    setActualAmount(centsToDollarInput(row.actualAmount));
    setNotes(row.notes ?? "");
  }, [row]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSave({
      monthKey: row.monthKey,
      category: row.category,
      estimatedAmount: dollarInputToCents(estimatedAmount),
      actualAmount: nullableDollarInputToCents(actualAmount),
      notes: notes.trim().length > 0 ? notes.trim() : null
    });
  }

  return (
    <tr>
      <td>{row.label}</td>
      <td>
        <form className="finance-cost-form" id={`cost-${row.id}`} onSubmit={handleSubmit}>
          <input
            aria-label={`${row.label} monthly estimate`}
            min="0"
            step="0.01"
            type="number"
            value={estimatedAmount}
            onChange={(event) => setEstimatedAmount(event.target.value)}
          />
        </form>
      </td>
      <td>
        <input
          aria-label={`${row.label} actual month-to-date`}
          form={`cost-${row.id}`}
          min="0"
          step="0.01"
          type="number"
          value={actualAmount}
          onChange={(event) => setActualAmount(event.target.value)}
        />
      </td>
      <td>
        <textarea
          aria-label={`${row.label} notes`}
          form={`cost-${row.id}`}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </td>
      <td>{formatDateTime(row.updatedAt)}</td>
      <td>{row.updatedBy ?? "Not updated"}</td>
      <td>
        <button className="secondary-button" disabled={isSaving} form={`cost-${row.id}`} type="submit">
          {isSaving ? "Saving" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function OperatingCostsTable({
  rows,
  onSave,
  savingCostId
}: {
  rows: CostTableRow[];
  onSave: (input: UpsertFounderFinanceOperatingCostInput) => void;
  savingCostId: string | null;
}) {
  return (
    <div className="table-shell">
      <table className="admin-table operating-cost-table">
        <thead>
          <tr>
            <th>Cost</th>
            <th>Monthly estimate</th>
            <th>Actual MTD</th>
            <th>Notes</th>
            <th>Last updated</th>
            <th>Updated by</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CostEditorRow
              key={row.id}
              isSaving={savingCostId === row.id}
              row={row}
              onSave={onSave}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RunRateCard({ row }: { row: FounderFinanceWindowSummary }) {
  const indicator = confidenceSignal(row);

  return (
    <section className={`forecast-card ${indicator}`}>
      <div className="forecast-card-header">
        <p className="card-title">{row.label}</p>
        <span className={`confidence-pill ${indicator}`}>{confidenceLabel(indicator)}</span>
      </div>
      <p className="forecast-net">{formatCents(row.projectedNet)}</p>
      <p className="finance-card-label">Projected net</p>
      <dl className="forecast-lines">
        <div>
          <dt>Revenue forecast</dt>
          <dd>{formatCents(row.projectedRevenue)}</dd>
        </div>
        <div>
          <dt>Cost forecast</dt>
          <dd>{formatCents(row.projectedCost)}</dd>
        </div>
      </dl>
      <p className="forecast-note">{confidenceDetail(indicator)}</p>
    </section>
  );
}

function RunRateTable({ rows }: { rows: FounderFinanceWindowSummary[] }) {
  return (
    <div className="table-shell">
      <table className="admin-table run-rate-table">
        <thead>
          <tr>
            <th>Window</th>
            <th>Revenue forecast</th>
            <th>Operating cost forecast</th>
            <th>Projected net</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const indicator = confidenceSignal(row);
            return (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{formatCents(row.projectedRevenue)}</td>
                <td>{formatCents(row.projectedCost)}</td>
                <td>{formatCents(row.projectedNet)}</td>
                <td>
                  <span className={`finance-signal ${indicator}`}>{confidenceLabel(indicator)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function FounderFinanceView({
  data,
  error,
  isLoading,
  onRefresh,
  onSaveCost,
  saveError,
  savingCostId
}: {
  data: FounderFinanceDashboardData | null;
  error: string;
  isLoading: boolean;
  onRefresh: () => void;
  onSaveCost: (input: UpsertFounderFinanceOperatingCostInput) => void;
  saveError: string;
  savingCostId: string | null;
}) {
  const [selectedStage, setSelectedStage] = useState<RevenuePipelineStage>("pending");

  if (isLoading && !data) {
    return <LoadingState message="Loading Founder Finance." />;
  }

  if (error && !data) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No Founder Finance data is available." />;
  }

  const costRows = currentMonthCostRows(data);
  const forecastRows = [data.currentMonthSummary, data.next30DaysForecast, data.next90DaysForecast];
  const pendingSummary = stageSummary(data, "pending");
  const lessRiskSummary = stageSummary(data, "past_cancellation_window");
  const awaitingPayoutSummary = stageSummary(data, "awaiting_payout");
  const paidOutSummary = stageSummary(data, "paid_out");
  const selectedBookingRows = data.drilldowns.revenuePipelineByStage[selectedStage] ?? [];
  const hasAnyBookings = hasBookings(data);
  const hasCosts = hasOperatingCosts(costRows);
  const hasForecast = hasAnyBookings || hasCosts;
  const answer = answerCopy(data.currentMonthSummary, hasForecast);
  const currentMonthSignal = confidenceSignal(data.currentMonthSummary);

  return (
    <div className="founder-finance-page">
      <FreshnessBar
        dataSource="fetchFounderFinanceDashboard"
        isLive={false}
        lastUpdated={dateFromIso(data.fetchedAt)}
        onRefresh={onRefresh}
      />

      {error ? <ErrorState message={error} /> : null}

      <section className="founder-finance-hero">
        <div>
          <p className="eyebrow">Founder view</p>
          <h2>Founder Finance</h2>
          <p>Daily founder check-in for revenue growth, marketplace health, and the path to $1M platform revenue.</p>
        </div>
        <aside className={`founder-answer ${answer.indicator}`}>
          <span>Is Chor earning enough to cover costs?</span>
          <strong>{answer.headline}</strong>
          <small>{answer.detail}</small>
        </aside>
      </section>

      <FounderGoalTracker data={data} />

      <MarketplaceHealthPanel health={data.marketplaceHealth} />

      <MarketplaceReleaseQueuePanel entries={data.marketplaceReleaseQueue} />

      <section className="finance-section founder-kpi-dashboard-section">
        <div className="finance-section-header">
          <div>
            <p className="eyebrow">Founder KPIs</p>
            <h2>Revenue growth and marketplace health</h2>
          </div>
          <p>Cards compare today or month-to-date performance against the prior month without fabricating growth from zero.</p>
        </div>
        {data.founderKpis.unavailable.length > 0 ? (
          <div className="finance-empty-state">
            {data.founderKpis.unavailable.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        ) : null}
        <div className="founder-kpi-dashboard-grid">
          {data.founderKpis.cards.map((card) => (
            <FounderKpiDashboardCard key={card.id} card={card} />
          ))}
        </div>
      </section>

      <section className="finance-kpi-strip" aria-label="Founder Finance key metrics">
        <KpiCard
          detail={bookingCountLabel(paidOutSummary.bookingCount)}
          label="Realized Platform Revenue"
          value={formatCents(paidOutSummary.netPlatformRevenue)}
        />
        <KpiCard
          detail={bookingCountLabel(pendingSummary.bookingCount)}
          label="At-Risk Revenue"
          value={formatCents(pendingSummary.netPlatformRevenue)}
        />
        <KpiCard
          detail={bookingCountLabel(lessRiskSummary.bookingCount)}
          label="Less-Risk Revenue"
          value={formatCents(lessRiskSummary.netPlatformRevenue)}
        />
        <KpiCard
          detail={bookingCountLabel(awaitingPayoutSummary.bookingCount)}
          label="Awaiting Payout"
          value={formatCents(awaitingPayoutSummary.netPlatformRevenue)}
        />
        <KpiCard
          detail={hasCosts ? "Forecasted from saved rows" : "No operating costs entered yet"}
          label="Monthly Operating Cost"
          value={formatCents(data.currentMonthSummary.projectedCost)}
        />
        <KpiCard
          detail={hasForecast ? confidenceLabel(currentMonthSignal) : "Forecast will appear once bookings or costs exist"}
          indicator={hasForecast ? currentMonthSignal : undefined}
          label="Projected Net This Month"
          value={hasForecast ? formatCents(data.currentMonthSummary.projectedNet) : "Not ready"}
        />
      </section>

      <section className="finance-section">
        <div className="finance-section-header">
          <div>
            <p className="eyebrow">Revenue Pipeline</p>
            <h2>What money is moving toward Chor?</h2>
          </div>
          <p>Tap a card to see the bookings behind it.</p>
        </div>
        {!hasAnyBookings ? <p className="finance-empty-state">No bookings yet</p> : null}
        <div className="finance-pipeline-grid">
          {PIPELINE_STAGES.map((stage) => (
            <PipelineCard
              key={stage}
              isSelected={selectedStage === stage}
              summary={stageSummary(data, stage)}
              onSelect={() => setSelectedStage(stage)}
            />
          ))}
        </div>
        <div className="section-table-header">
          <div>
            <p className="card-title">Drill-down</p>
            <h3>{stageLabel(selectedStage)}</h3>
          </div>
        </div>
        <BookingTable rows={selectedBookingRows} />
      </section>

      <section className="finance-section">
        <div className="finance-section-header">
          <div>
            <p className="eyebrow">Operating Costs</p>
            <h2>What does Chor cost to run this month?</h2>
          </div>
          <p>Update estimates as bills arrive so the forecast stays honest.</p>
        </div>
        {saveError ? <p className="form-error">{saveError}</p> : null}
        {!hasCosts ? <p className="finance-empty-state">No operating costs entered yet</p> : null}
        <OperatingCostsTable rows={costRows} savingCostId={savingCostId} onSave={onSaveCost} />
      </section>

      <section className="finance-section">
        <div className="finance-section-header">
          <div>
            <p className="eyebrow">Net Run Rate</p>
            <h2>Is revenue trending above costs?</h2>
          </div>
          <p>Bookings are weighted by how likely they are to become revenue.</p>
        </div>
        {!hasForecast ? (
          <p className="finance-empty-state">Forecast will appear once bookings or costs exist</p>
        ) : (
          <>
            <div className="forecast-card-grid">
              {forecastRows.map((row) => (
                <RunRateCard key={row.label} row={row} />
              ))}
            </div>
            <div className="section-table-header">
              <div>
                <p className="card-title">Forecast detail</p>
                <h3>Revenue forecast vs. operating costs</h3>
              </div>
            </div>
            <RunRateTable rows={forecastRows} />
          </>
        )}
      </section>
    </div>
  );
}
